export const PAYLOAD_PREFIX = "payload-";

export type MeasureSpec = number | "match-parent" | "wrap-content" | "preserve-aspect";
export type CollapseType = "replace" | "expose";
export type ItemType = Source.FLYER_ITEM | Source.ECOM_ITEM | Source.COUPON | Source.OFFER;
export type FontWeight = "bold" | "normal";
export type ImagePosition = "image-top" | "image-left" | "image-right";

export enum Source {
  FLYER_ITEM = "flyer-item",
  ECOM_ITEM = "ecom-item",
  COUPON = "coupon",
  OFFER = "offer",
}

export enum Action {
  LINK = "link"
}

export interface ItemIdentifier {
  itemType: ItemType;
  itemId: string;
}

export interface UIElementBase {
  readonly width: MeasureSpec;
  readonly height: MeasureSpec;
  readonly aspectRatio: number | null;
  readonly backgroundColor: Color | null;
  readonly anchorId: string | null;
  readonly attachedProperties: { [key: string]: string };
  readonly padding: Margin | null;
  readonly margin: Margin | null;
  readonly borderWidth: Margin | null;
  readonly borderColor: Color | null;
  readonly styleId: string | null;
  readonly flexGrow?: number | null;
}

export type UIElement =
  | StorefrontElement
  | ScrollViewElement
  | LinearLayoutElement
  | CollapsibleLayoutElement
  | FlexboxElement
  | ImageElement
  | ItemAtomElement
  | BadgeElement
  | WayfinderElement
  | WayfinderCategoryElement;

export interface WithBadages {
  readonly badges: BadgeElement[];
}

export interface WithTexts {
  readonly texts: TextElement[];
}

export interface ScrollViewElement extends UIElementBase {
  readonly kind: "scrollview";
  readonly orientation: "horizontal" | "vertical";
  readonly content: UIElement;
}

export interface LinearLayoutElement extends UIElementBase {
  readonly kind: "linear-layout";
  readonly orientation: "horizontal" | "vertical";
  readonly content: UIElement[];
}

export interface CollapsibleLayoutElement extends UIElementBase {
  readonly kind: "collapsible-layout";
  readonly orientation: "horizontal" | "vertical";
  readonly content: UIElement[];
  readonly title: string;
  readonly exposedTitle: string;
  readonly icon: string | null;
  readonly iconUrl: string | null;
  readonly collapseType: CollapseType;
}

export interface FlexboxElement extends UIElementBase {
  readonly kind: "flexbox";
  readonly orientation: "horizontal" | "vertical";
  readonly content: UIElement[];
}

export interface ItemElementBase {
  readonly itemType: ItemType;
  readonly itemId: string;
  readonly targetAnchorId: string | null;
  readonly payload: Map<string, string>;
}

export interface BadgeElement {
  readonly kind: "badge";
  readonly hpos: number;
  readonly vpos: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly url: string;
  readonly attachedProperties: { [key: string]: string };
}

export interface ItemAtomElement extends UIElementBase, ItemElementBase, WithBadages, WithTexts {
  readonly kind: "item-atom";
  readonly width: MeasureSpec;
  readonly height: MeasureSpec;
  readonly imagePosition: ImagePosition;
  readonly imageUrl: string;
  readonly imageWidth: number;
  readonly imageHeight: number;
}

export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface Color {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

export interface AreaElement extends ItemElementBase {
  readonly href: string;
  readonly label: string;
  readonly rect: Rect;
  readonly action: ActionElement | null;
}

export interface ActionElement {
  readonly type: Action;
  readonly ttmUrl: string | null;
}

export interface Margin {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export type SourceElement = FlyerSourceElement | UrlSourceElement;

export interface SourceElementBase {
  readonly kind: "source";
  readonly width: number;
  readonly height: number;
}

export interface FlyerSourceElement extends SourceElementBase {
  readonly format: "tiled";
  readonly path: string;
  readonly resolutions: number[];
  readonly rect: Rect;
  readonly areas: AreaElement[];
}

export interface UrlSourceElement extends SourceElementBase {
  readonly format: "url";
  readonly url: string;
  readonly areas: AreaElement[];
}

export interface ImageElement extends UIElementBase, WithBadages {
  readonly kind: "image";
  readonly sources: SourceElement[];
}

export interface TextElement extends UIElementBase {
  readonly kind: "text";
  readonly value: string;
  readonly maxLines: number;
}

export interface WayfinderElement {
  readonly kind: "wayfinder";
  readonly categories: WayfinderCategoryElement[];
  readonly attachedProperties: { [key: string]: string };
}

export interface WayfinderCategoryElement {
  readonly kind: "category";
  readonly name: string;
  readonly anchorId: string;
  readonly attachedProperties: { [key: string]: string };
}

export interface StyleElement {
  readonly kind: "style";
  readonly styles: StyleTypeElement[];
}

export interface StyleTypeElement {
  readonly id: string;
  readonly paddingStyle: PaddingStyleElement | null;
  readonly marginStyle: MarginStyleElement | null;
  readonly backgroundStyle: BackgroundStyleElement | null;
  readonly borderStyle: BorderStyleElement | null;
  readonly textStyle: TextStyleElement | null;
  readonly attachedProperties: { [key: string]: string };
}

export interface PaddingStyleElement {
  readonly kind: "padding-style";
  readonly padding: Margin | null;
}

export interface MarginStyleElement {
  readonly kind: "margin-style";
  readonly margin: Margin | null;
}

export interface BackgroundStyleElement {
  readonly kind: "background-style";
  readonly color: Color | null;
  readonly url: string | null;
}

export interface BorderStyle {
  readonly color: string | null;
  readonly style: string | null; // "dashed" | "solid" | "dotted"
  readonly width: string | null;
}

export interface BorderStyleElement {
  readonly kind: "border-style";
  readonly left: BorderStyle | null;
  readonly top: BorderStyle | null;
  readonly right: BorderStyle | null;
  readonly bottom: BorderStyle | null;
}

export interface TextStyleElement {
  readonly kind: "text-style";
  readonly fontFamily: string | null;
  readonly fontWeight: FontWeight | null;
  readonly fontColor: Color | null;
  readonly fontSize: number | null;
}

export interface StorefrontElement {
  readonly kind: "storefront";
  readonly version: string;
  readonly title: string | null;
  readonly subtitle: string | null;
  readonly uuid: string | null;
  readonly content: UIElement;
  readonly wayfinder: WayfinderElement | null;
  readonly style: StyleElement | null;
  readonly attachedProperties: { [key: string]: string };
}

export function instanceOfUIElement(object: any): object is UIElement {
  return (
    object.kind === "storefront" ||
    object.kind === "scrollview" ||
    object.kind === "linear-layout" ||
    object.kind === "collapsible-layout" ||
    object.kind === "flexbox" ||
    object.kind === "image" ||
    object.kind === "item-atom" ||
    object.kind === "text"
  );
}

export function instanceOfUIElementBase(object: any): object is UIElementBase {
  return (
    object.kind === "scrollview" ||
    object.kind === "wayfinder" ||
    object.kind === "category" ||
    object.kind === "linear-layout" ||
    object.kind === "collapsible-layout" ||
    object.kind === "flexbox" ||
    object.kind === "image" ||
    object.kind === "item-atom" ||
    object.kind === "text"
  );
}

export function instanceOfUIElementContainer(
  object: any,
): object is LinearLayoutElement | CollapsibleLayoutElement | FlexboxElement {
  return object.kind === "linear-layout" || object.kind === "collapsible-layout" || object.kind === "flexbox";
}

export function instanceOfItemAtomElement(object: any): object is ItemAtomElement {
  return object.kind === "item-atom";
}

export function instanceOfImageElementBase(object: any): object is ImageElement {
  return object.kind === "image";
}

export function instanceOfFlyerSourceElement(object: any): object is FlyerSourceElement {
  return object.format === "tiled";
}



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/parse/sfml-ast.ts